<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="deleteAttachmentDialog"
            :data="currentAttachment"
            :action-loading="deleteAttachmentLoading"
            removal
            @on-confirm="deleteComplaintAttachment(currentAttachment.id)"
            @on-abort="closeConfirmDeleteAttachmentDialog"
        >
            <template #title>
                Usuwanie załącznika
            </template>
            <template #content>
                Czy na pewno chcesz usunąć załącznik: <strong>{{ getFileName(currentAttachment) }}</strong>?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-card flat>
            <v-overlay
                color="white"
                opacity="0.85"
                absolute
                :value="fileProgress.show"
            >
                <v-container class="pa-0">
                    <v-row justify="center">
                        <v-col cols="auto">
                            <v-progress-circular
                                :rotate="-90"
                                :size="120"
                                :width="4"
                                :value="fileProgress.value"
                                color="primary"
                            >
                                {{ fileProgress.value }}
                            </v-progress-circular>
                        </v-col>
                    </v-row>
                    <v-row justify="center">
                        <span class="text-caption primary--text">{{ fileProgress.fileName }}</span>
                    </v-row>
                    <v-row justify="center">
                        <v-col cols="auto">
                            <v-btn
                                color="error"
                                small
                                outlined
                                data-sentry-label="Anuluj"
                                @click="abortUploadAttachments(fileProgress.fileName)"
                            >
                                Anuluj
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-overlay>
            <v-card-text>
                <v-container class="pa-0">
                    <v-row>
                        <v-col cols="auto" sm="8" md="6" lg="8">
                            <v-file-input
                                v-model="currentFiles"
                                label="Pliki"
                                multiple
                                counter
                                placeholder="Wybierz plik"
                                prepend-icon="mdi-paperclip"
                                outlined
                                dense
                                :show-size="1000"
                                :error-messages="currentComplaintAttachmentsErrors"
                                :disabled="!canSaveComplaint || isComplaintIneditable"
                            >
                                <template #selection="{text}">
                                    <v-chip
                                        label
                                        small
                                    >
                                        {{ text }}
                                    </v-chip>
                                </template>
                            </v-file-input>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                color="primary"
                                outlined
                                data-sentry-label="Wyślij plik"
                                :disabled="currentFiles.length === 0 || !canSaveComplaint || isComplaintIneditable"
                                @click="uploadAttachments"
                            >
                                <v-icon left>
                                    mdi-file-upload
                                </v-icon>
                                Wyślij
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container class="pa-0">
                    <v-row>
                        <v-col cols="auto" sm="12" md="6" lg="8">
                            <v-data-table
                                v-if="uploadedFiles.length > 0"
                                :items="uploadedFiles"
                                :headers="headers"
                                disable-pagination
                                hide-default-footer
                                :mobile-breakpoint="0"
                            >
                                <template #item.fileName="{item}">
                                    {{ parseFileName(item.file) }}
                                </template>
                                <template #item.actions="{item}">
                                    <div class="d-inline-flex">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Pobierz"
                                                    v-on="on"
                                                    @click="openInNewTab(item)"
                                                >
                                                    <v-icon color="primary">
                                                        mdi-download
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Pobierz</span>
                                        </v-tooltip>
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Usuń"
                                                    :disabled="!canSaveComplaint"
                                                    v-on="on"
                                                    @click="showDeleteAttachmentDialog(item)"
                                                >
                                                    <v-icon color="pink">
                                                        mdi-delete
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Usuń</span>
                                        </v-tooltip>
                                    </div>
                                </template>
                            </v-data-table>

                            <v-alert
                                v-else
                                type="info"
                                text
                                border="left"
                            >
                                Nie dodano załączników.
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import { appConfig, apiConfig } from '@/config'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'
import { errorHandler } from '@/utils/errorHandler'
import ConfirmDialog from '@/components/common/ConfirmDialog'

const maxFileSize = (value) => {
    return value.size < appConfig.complaints.complaintAttachmentSize * 1024 * 1024
}

export default {
    name: 'Attachment',
    components: {
        ConfirmDialog
    },
    mixins: [
        tooltipDelay, parse
    ],
    props: {
        complaint: {
            type: Object,
            default: null
        },
        uploadedFiles: {
            type: Array,
            default: () => []
        },
        canSaveComplaint: {
            type: Boolean
        }
    },
    data() {
        return {
            currentFiles: [],
            headers: [
                {
                    text: 'Nazwa pliku',
                    value: 'fileName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ],
            deleteAttachmentDialog: false,
            deleteAttachmentLoading: false,
            currentAttachment: {},
        }
    },
    validations() {
        const validations = {}

        if (this.currentFiles.length > 0) { //TODO: Obsługa błędów
            validations['currentFiles'] = {
                $each: {
                    maxFileSize
                }
            }
        }

        return validations
    },
    computed: {
        fileProgress() {
            return this.$store.state.common.progress
        },
        userId() {
            return this.$store.state.user.userId
        },
        isDraftComplaint() {
            return this.complaint.complaintStatusId === 15 // robocza
        },
        isComplaintIneditable() {
            return this.isDraftComplaint && (this.complaint.creator.id != this.userId)
        },
        // Walidacje
        currentComplaintAttachmentsErrors() {
            if (this.currentFiles.length === 0) return

            const errors = []

            if (!this.$v.currentFiles.$dirty) return errors
            this.$v.currentFiles.$invalid && errors.push(`Przekroczono dopuszczalny rozmiar pliku: ${appConfig.complaints.complaintAttachmentSize}MB.`)

            return errors
        },
    },
    methods: {
        getFileName(attachment) {
            if (attachment.file !== null && attachment.file !== undefined) {
                return this.parseFileName(attachment.file)
            }

            return ''
        },
        uploadAttachments() {
            this.$v.currentFiles.$touch()

            if (this.$v.currentFiles.$invalid) return

            const uploadSingleFile = () => {
                if (this.currentFiles.length > 0) {
                    const formData = new FormData()

                    formData.append('file', this.currentFiles[0])

                    this.$store.dispatch('complaints/createAttachment', {
                        formData: formData,
                        fileName: Object.fromEntries(formData).file.name
                    })
                        .then(response => {
                            this.$emit('add-complaint-attachment', response.data)
                            this.currentFiles.shift()
                            uploadSingleFile()
                        })
                        .catch(error => {
                            if (error.message !== 'canceled') {
                                errorHandler(error)
                            }
                        })
                }
            }

            uploadSingleFile()
        },
        abortUploadAttachments(fileName) {
            this.$store.dispatch('complaints/createAttachment', {
                formData: null,
                fileName: fileName
            })
                .catch(error => {
                    if (error.message !== 'canceled') {
                        errorHandler(error)
                    }
                })
        },
        deleteComplaintAttachment(id) {
            this.deleteAttachmentLoading = true
            this.$store.dispatch('complaints/deleteAttachment', id)
                .then(() => {
                    this.$emit('delete-complaint-attachment', id)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmDeleteAttachmentDialog()
                })
        },
        showDeleteAttachmentDialog(attachment) {
            this.currentAttachment = attachment
            this.deleteAttachmentDialog = true
        },
        closeConfirmDeleteAttachmentDialog() {
            this.deleteAttachmentDialog = false
            this.deleteAttachmentLoading = false
            this.currentAttachment = {}
        },
        openInNewTab(file) {
            const mediaRoot = apiConfig.FACTORYBASE_API_URI

            window.open(`${mediaRoot}${file.file}`, '_blank').focus()
        },
    }
}
</script>
